import { useState, useEffect, useMemo } from "react";
import { BsChevronLeft, BsChevronRight, BsCameraFill, BsExclamationTriangle } from "react-icons/bs";
import { useAuthContext } from "../../context/auth";
import { getInventarioInfra, construirInventarioInfra, TIPO_PANTALLA_LABEL, ESTADO_PANTALLA_LABEL } from "../../utils";
import { Loader } from "../Loader";
import styles from "./styles.module.scss";

// Vista MOBILE-FIRST (100% teléfono) de la ronda de mantenimiento.
// Fase A: SOLO navegación con datos reales. Sin carga de foto ni escritura.
// Jerarquía: Mi ronda (bloques) → edificios → pantallas → detalle de pantalla.

const badgeEstadoClass = (estado) => {
  if (estado === "operativa") return styles.badgeOperativa;
  if (estado === "precisa_mantenimiento") return styles.badgePrecisa;
  return styles.badgeNeutro;
};

// Header con botón "volver" (o título pelado en el nivel raíz).
const Header = ({ titulo, subtitulo, onBack }) => (
  <header className={styles.header}>
    {onBack ? (
      <button type="button" className={styles.backBtn} onClick={onBack} aria-label="Volver">
        <BsChevronLeft />
      </button>
    ) : (
      <span className={styles.headerSpacer} />
    )}
    <div className={styles.headerTitulo}>
      <span className={styles.headerNombre}>{titulo}</span>
      {subtitulo ? <span className={styles.headerSub}>{subtitulo}</span> : null}
    </div>
  </header>
);

// Fila táctil grande, reutilizable en cada nivel de lista.
const Fila = ({ nombre, meta, extra, onClick }) => (
  <button type="button" className={styles.fila} onClick={onClick}>
    <div className={styles.filaTexto}>
      <span className={styles.filaNombre}>{nombre}</span>
      {meta ? <span className={styles.filaMeta}>{meta}</span> : null}
    </div>
    <div className={styles.filaDerecha}>
      {extra}
      <BsChevronRight className={styles.filaChevron} />
    </div>
  </button>
);

const InfraRondaMobile = () => {
  const { pais } = useAuthContext();
  const [loading, setLoading] = useState(true);
  const [raw, setRaw] = useState({ bloques: [], edificios: [], pantallas: [], errores: [] });

  // Selección de navegación (por id — 10.7).
  const [bloqueId, setBloqueId] = useState(null);
  const [edificioId, setEdificioId] = useState(null);
  const [pantallaId, setPantallaId] = useState(null);

  useEffect(() => {
    let vivo = true;
    setLoading(true);
    getInventarioInfra(pais)
      .then((data) => vivo && setRaw(data))
      .finally(() => vivo && setLoading(false));
    return () => {
      vivo = false;
    };
  }, [pais]);

  const inv = useMemo(() => construirInventarioInfra(raw), [raw]);

  // Grupos navegables: bloques + (si hubiera) el grupo "sin bloque".
  const grupos = useMemo(() => {
    const gs = inv.gruposBloque.map((g) => ({
      id: g.bloque.id,
      nombre: g.bloque.nombre,
      esSatelite: g.bloque.es_satelite,
      edificios: g.edificios,
      totalEdificios: g.totalEdificios,
      totalPantallas: g.totalPantallas,
    }));
    if (inv.sinBloque.totalEdificios > 0) {
      gs.push({
        id: "sin-bloque",
        nombre: "Sin bloque",
        esSatelite: false,
        edificios: inv.sinBloque.edificios,
        totalEdificios: inv.sinBloque.totalEdificios,
        totalPantallas: inv.sinBloque.totalPantallas,
      });
    }
    return gs;
  }, [inv]);

  const grupoSel = useMemo(() => grupos.find((g) => String(g.id) === String(bloqueId)) || null, [grupos, bloqueId]);
  const edificioSel = useMemo(
    () => grupoSel?.edificios.find((e) => String(e.id) === String(edificioId)) || null,
    [grupoSel, edificioId]
  );
  const pantallaSel = useMemo(
    () => edificioSel?.pantallas.find((p) => String(p.id) === String(pantallaId)) || null,
    [edificioSel, pantallaId]
  );

  if (loading) {
    return (
      <div className={styles.pantalla}>
        <Loader />
      </div>
    );
  }

  const vacio = inv.totales.pantallas === 0 && inv.totales.bloques === 0;
  if (vacio) {
    return (
      <div className={styles.pantalla}>
        <Header titulo="Mi ronda" />
        <div className={styles.empty}>
          <BsExclamationTriangle className={styles.emptyIcon} />
          <p>{raw.errores?.length ? "No se pudo cargar el inventario." : "No hay pantallas para tu país."}</p>
        </div>
      </div>
    );
  }

  // ── Nivel 3: detalle de pantalla (maqueta de fase B, sin funcionalidad) ──
  if (pantallaSel) {
    return (
      <div className={styles.pantalla}>
        <Header
          titulo={pantallaSel.etiqueta || "Pantalla"}
          subtitulo={`${edificioSel.edificio} · ${TIPO_PANTALLA_LABEL[pantallaSel.tipo] || pantallaSel.tipo}`}
          onBack={() => setPantallaId(null)}
        />
        <div className={styles.detalle}>
          <div className={styles.detalleEstado}>
            <span className={styles.detalleLabel}>Estado actual</span>
            <span className={`${styles.badge} ${badgeEstadoClass(pantallaSel.estado)}`}>
              {ESTADO_PANTALLA_LABEL[pantallaSel.estado] || pantallaSel.estado}
            </span>
          </div>

          {/* Maqueta de la foto — fase B */}
          <div className={styles.fotoPlaceholder} aria-disabled="true">
            <BsCameraFill className={styles.fotoIcon} />
            <span>Foto de la visita</span>
            <span className={styles.proximamente}>Disponible en la próxima fase</span>
          </div>

          {/* Maqueta de los botones de estado — fase B, deshabilitados */}
          <div className={styles.accionesEstado}>
            <span className={styles.detalleLabel}>Registrar estado</span>
            <div className={styles.botonesEstado}>
              <button type="button" className={styles.btnEstadoOk} disabled>
                Operativa
              </button>
              <button type="button" className={styles.btnEstadoMant} disabled>
                Precisa mantenimiento
              </button>
            </div>
            <p className={styles.nota}>La carga de foto y estado se habilita en la próxima fase.</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Nivel 2: pantallas del edificio ──
  if (edificioSel) {
    return (
      <div className={styles.pantalla}>
        <Header
          titulo={edificioSel.edificio}
          subtitulo={edificioSel.ubicacion || grupoSel.nombre}
          onBack={() => setEdificioId(null)}
        />
        <div className={styles.lista}>
          {edificioSel.pantallas.length === 0 ? (
            <div className={styles.empty}>
              <p>Este edificio no tiene pantallas cargadas.</p>
            </div>
          ) : (
            edificioSel.pantallas.map((p) => (
              <Fila
                key={p.id}
                nombre={p.etiqueta || "Pantalla"}
                meta={TIPO_PANTALLA_LABEL[p.tipo] || p.tipo}
                extra={
                  <span className={`${styles.badge} ${badgeEstadoClass(p.estado)}`}>
                    {ESTADO_PANTALLA_LABEL[p.estado] || p.estado}
                  </span>
                }
                onClick={() => setPantallaId(p.id)}
              />
            ))
          )}
        </div>
      </div>
    );
  }

  // ── Nivel 1: edificios del bloque ──
  if (grupoSel) {
    return (
      <div className={styles.pantalla}>
        <Header
          titulo={grupoSel.nombre}
          subtitulo={`${grupoSel.totalEdificios} edificios · ${grupoSel.totalPantallas} pantallas`}
          onBack={() => setBloqueId(null)}
        />
        <div className={styles.lista}>
          {grupoSel.edificios.map((e) => (
            <Fila
              key={e.id}
              nombre={e.edificio}
              meta={e.ubicacion || ""}
              extra={<span className={styles.progreso}>0/{e.totalPantallas}</span>}
              onClick={() => setEdificioId(e.id)}
            />
          ))}
        </div>
      </div>
    );
  }

  // ── Nivel 0: Mi ronda (bloques) ──
  return (
    <div className={styles.pantalla}>
      <Header titulo="Mi ronda" subtitulo={`${inv.totales.pantallas} pantallas en ${grupos.length} bloques`} />
      <div className={styles.lista}>
        {grupos.map((g) => (
          <Fila
            key={g.id}
            nombre={g.nombre}
            meta={`${g.totalEdificios} edificios · ${g.totalPantallas} pantallas`}
            extra={g.esSatelite ? <span className={styles.satelite}>quincenal</span> : null}
            onClick={() => setBloqueId(g.id)}
          />
        ))}
      </div>
    </div>
  );
};

export default InfraRondaMobile;
